// ============================================
// Garcia Yturria — Shopify Theme JS
// ============================================

(() => {
  'use strict';

  // --- Scroll Animations (IntersectionObserver) ---
  const initAnimations = () => {
    const elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const delay = parseInt(el.dataset.delay) || 0;
          setTimeout(() => el.classList.add('is-visible'), delay);
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    elements.forEach(el => observer.observe(el));
  };

  // --- Navbar Scroll Effect (hide on scroll down, show on scroll up) ---
  const initNavbar = () => {
    const navbar = document.querySelector('.navbar');
    const navInline = document.querySelector('.nav-inline');
    if (!navbar) return;

    let lastScroll = 0;
    let ticking = false;

    const update = () => {
      const scrollY = window.scrollY;
      const isScrollingDown = scrollY > lastScroll && scrollY > 100;

      navbar.classList.toggle('scrolled', scrollY > 50);
      navbar.classList.toggle('navbar--hidden', isScrollingDown && scrollY > 200);
      if (navInline) navInline.classList.toggle('nav-inline--hidden', isScrollingDown && scrollY > 200);

      lastScroll = scrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  };

  // --- Hero Slider ---
  const initHeroSlider = () => {
    const slides = document.querySelectorAll('.hero__slide');
    const dots = document.querySelectorAll('.hero__dot');
    if (slides.length < 2) return;

    let current = 0;
    let interval;

    const goTo = (index) => {
      slides[current].classList.remove('active');
      if (dots[current]) dots[current].classList.remove('active');
      current = index;
      slides[current].classList.add('active');
      if (dots[current]) dots[current].classList.add('active');
    };

    const next = () => {
      goTo((current + 1) % slides.length);
    };

    const startAutoplay = () => {
      interval = setInterval(next, 5000);
    };

    const stopAutoplay = () => {
      clearInterval(interval);
    };

    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        stopAutoplay();
        goTo(parseInt(dot.dataset.slide));
        startAutoplay();
      });
    });

    startAutoplay();
  };

  // --- Mobile Menu Toggle (drawer) ---
  const initMenu = () => {
    const toggle = document.querySelector('.menu-toggle');
    const drawer = document.getElementById('nav-drawer');
    const backdrop = document.getElementById('nav-backdrop');
    const closeBtn = document.getElementById('nav-drawer-close');
    if (!toggle || !drawer) return;

    const openMenu = () => {
      drawer.classList.add('active');
      if (backdrop) backdrop.classList.add('active');
      document.body.classList.add('menu-open');
      toggle.setAttribute('aria-expanded', 'true');
    };

    const closeMenu = () => {
      drawer.classList.remove('active');
      if (backdrop) backdrop.classList.remove('active');
      document.body.classList.remove('menu-open');
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', openMenu);
    if (closeBtn) closeBtn.addEventListener('click', closeMenu);
    if (backdrop) backdrop.addEventListener('click', closeMenu);

    // Submenu toggles
    drawer.querySelectorAll('.nav-drawer__toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const sub = btn.nextElementSibling;
        const isOpen = sub.classList.contains('open');
        // Close all
        drawer.querySelectorAll('.nav-drawer__sub').forEach(s => s.classList.remove('open'));
        drawer.querySelectorAll('.nav-drawer__toggle').forEach(b => b.setAttribute('aria-expanded', 'false'));
        // Open clicked
        if (!isOpen) {
          sub.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Close on link click
    drawer.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', closeMenu);
    });
  };

  // --- Smooth Scroll for Anchor Links ---
  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href === '#') return;
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          const offset = document.querySelector('.navbar')?.offsetHeight || 72;
          window.scrollTo({
            top: target.getBoundingClientRect().top + window.scrollY - offset,
            behavior: 'smooth'
          });
        }
      });
    });
  };

  // --- Newsletter Form ---
  const initNewsletter = () => {
    const form = document.querySelector('.newsletter__form');
    if (!form) return;

    // Find the parent Shopify form ({% form 'customer' %} wraps .newsletter__form)
    const shopifyForm = form.closest('form');

    if (shopifyForm) {
      // Shopify form exists — let it submit naturally, show success via AJAX
      shopifyForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        if (!input || !input.value) return;

        const formData = new FormData(shopifyForm);
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) {
          submitBtn.textContent = 'Enviando...';
          submitBtn.disabled = true;
        }

        fetch(shopifyForm.action || '/contact', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' }
        }).then(() => {
          form.style.display = 'none';
          const msg = document.createElement('p');
          msg.className = 'newsletter__success';
          msg.textContent = '¡Gracias! Pronto recibirás nuestras novedades.';
          form.parentElement.appendChild(msg);
        }).catch(() => {
          // Even on error, show success (Shopify may redirect)
          form.style.display = 'none';
          const msg = document.createElement('p');
          msg.className = 'newsletter__success';
          msg.textContent = '¡Gracias! Pronto recibirás nuestras novedades.';
          form.parentElement.appendChild(msg);
        });
      });
    } else {
      // Fallback for static pages without Shopify form
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        if (input && input.value) {
          form.style.display = 'none';
          const msg = document.createElement('p');
          msg.className = 'newsletter__success';
          msg.textContent = '¡Gracias! Pronto recibirás nuestras novedades.';
          form.parentElement.appendChild(msg);
        }
      });
    }
  };

  // --- Image Lazy Load Reveal (lightweight, no forced reflow) ---
  const initImageReveal = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('img-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05 });

    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      if (!img.complete) {
        img.classList.add('img-lazy');
        observer.observe(img);
        img.addEventListener('load', () => img.classList.add('img-revealed'), { once: true });
      }
    });
  };

  // --- Product Gallery Thumbnail Switching ---
  const initProductGallery = () => {
    const mainImg = document.getElementById('product-main-img');
    const thumbs = document.querySelectorAll('.product-thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        const newSrc = thumb.dataset.img || thumb.querySelector('img')?.src;
        if (newSrc) {
          mainImg.style.opacity = '0';
          setTimeout(() => {
            mainImg.src = newSrc;
            mainImg.style.opacity = '1';
          }, 200);
        }
      });
    });
  };

  // --- Quantity Selector ---
  const initQuantity = () => {
    document.querySelectorAll('.quantity-selector').forEach(selector => {
      const input = selector.querySelector('input');
      const minus = selector.querySelector('[data-action="minus"]');
      const plus = selector.querySelector('[data-action="plus"]');
      if (!input) return;

      if (minus) {
        minus.addEventListener('click', () => {
          const val = parseInt(input.value) || 1;
          if (val > 1) input.value = val - 1;
        });
      }
      if (plus) {
        plus.addEventListener('click', () => {
          const val = parseInt(input.value) || 1;
          input.value = val + 1;
        });
      }
    });
  };

  // --- Variant Option Buttons ---
  const initVariantButtons = () => {
    document.querySelectorAll('.product-options__values').forEach(group => {
      group.querySelectorAll('.product-options__btn').forEach(btn => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('.product-options__btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
        });
      });
    });
  };

  // --- Wishlist ---
  const initWishlist = () => {
    const STORAGE_KEY = 'gy_wishlist';

    const getWishlist = () => JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const saveWishlist = (items) => localStorage.setItem(STORAGE_KEY, JSON.stringify(items));

    const updateCount = () => {
      const count = getWishlist().length;
      const countEl = document.getElementById('wishlist-count');
      if (countEl) {
        if (count > 0) {
          countEl.textContent = count;
          countEl.style.display = 'flex';
        } else {
          countEl.style.display = 'none';
        }
      }
    };

    const showToast = (added) => {
      const toast = document.getElementById('wishlist-toast');
      const text = document.getElementById('wishlist-toast-text');
      if (!toast) return;
      text.textContent = added ? 'Agregado a favoritos' : 'Eliminado de favoritos';
      toast.classList.add('show');
      setTimeout(() => toast.classList.remove('show'), 3500);
    };

    const isInWishlist = (id) => getWishlist().some(item => item.id === String(id));

    const toggleWishlist = (btn) => {
      const id = String(btn.dataset.productId);
      let items = getWishlist();
      let added = false;

      if (isInWishlist(id)) {
        items = items.filter(item => item.id !== id);
        btn.classList.remove('active');
      } else {
        items.push({
          id: id,
          title: btn.dataset.productTitle,
          url: btn.dataset.productUrl,
          image: btn.dataset.productImage,
          price: btn.dataset.productPrice
        });
        btn.classList.add('active');
        added = true;
      }

      saveWishlist(items);
      updateCount();
      showToast(added);

      // Update label if product page button
      const label = btn.querySelector('.wishlist-btn-product__label');
      if (label) {
        label.textContent = added ? 'En tus favoritos' : 'Agregar a favoritos';
      }
    };

    // Init card buttons (collection grid)
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      if (isInWishlist(btn.dataset.productId)) {
        btn.classList.add('active');
      }
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleWishlist(btn);
      });
    });

    // Init product page button
    document.querySelectorAll('.wishlist-btn-product').forEach(btn => {
      if (isInWishlist(btn.dataset.productId)) {
        btn.classList.add('active');
        const label = btn.querySelector('.wishlist-btn-product__label');
        if (label) label.textContent = 'En tus favoritos';
      }
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        toggleWishlist(btn);
      });
    });

    updateCount();
  };

  // --- Init All ---
  document.addEventListener('DOMContentLoaded', () => {
    initAnimations();
    initNavbar();
    initHeroSlider();
    initMenu();
    initSmoothScroll();
    initNewsletter();
    initImageReveal();
    initProductGallery();
    initQuantity();
    initVariantButtons();
    initWishlist();
    initCartDrawer();
  });

  // --- Cart Drawer (AJAX Add to Cart) ---
  const initCartDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    const backdrop = document.getElementById('cart-drawer-backdrop');
    const closeBtn = document.getElementById('cart-drawer-close');
    const continueBtn = document.getElementById('cart-drawer-continue');
    const form = document.getElementById('product-form');

    if (!drawer) return;

    const openDrawer = (item) => {
      // Populate drawer with product info
      const imgEl = document.getElementById('cart-drawer-img');
      const nameEl = document.getElementById('cart-drawer-name');
      const variantEl = document.getElementById('cart-drawer-variant');
      const priceEl = document.getElementById('cart-drawer-price');

      if (imgEl && item.image) {
        imgEl.innerHTML = `<img src="${item.image}" alt="${item.title}">`;
      }
      if (nameEl) nameEl.textContent = item.product_title || item.title;
      if (variantEl) {
        const v = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        variantEl.textContent = v;
      }
      if (priceEl) priceEl.textContent = '$' + (item.price / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });

      // Update cart count in nav
      fetch('/cart.js').then(r => r.json()).then(cart => {
        const countEl = document.querySelector('.cart-count');
        if (countEl) {
          countEl.textContent = cart.item_count;
        } else {
          const navBtn = document.querySelector('.nav-btn[aria-label="Carrito"]');
          if (navBtn) {
            const span = document.createElement('span');
            span.className = 'cart-count';
            span.textContent = cart.item_count;
            navBtn.appendChild(span);
          }
        }
      });

      drawer.classList.add('active');
      backdrop.classList.add('active');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.classList.remove('active');
      backdrop.classList.remove('active');
      document.body.style.overflow = '';
    };

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (continueBtn) continueBtn.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);

    // Intercept form submit
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Agregando...';
        submitBtn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        })
        .then(r => r.json())
        .then(item => {
          submitBtn.textContent = '✓ Agregado';
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 2000);
          openDrawer(item);
        })
        .catch(() => {
          submitBtn.textContent = 'Error - Intenta de nuevo';
          submitBtn.disabled = false;
          setTimeout(() => { submitBtn.textContent = originalText; }, 2000);
        });
      });
    }
  };

})();
